/**
 * The Content Browser's view model (SPEC §5), from the content index: folders
 * with counts, and what a folder shows. Folders are separate views, as in
 * Unreal and Unity: the Content root shows the top-level folders, a level
 * folder shows its levels in index order (roles newest first, decided when the
 * index is built), and an asset folder shows its subfolders, then its assets.
 *
 * Asset folders mirror the folders on disk (`assets/models/` is Assets ›
 * Models), as Unreal's do, named with a capital like the level folders. The
 * Type filter is a separate view: everything of one type under a folder,
 * flat, as Unreal's filters search subfolders.
 *
 * A skin set shows as its default model, noting how many skins it has; the
 * other skins are picked in the Inspector's Skin row (assets/skinSets).
 */
import { folderForKind, LEVEL_FOLDERS } from "../../assets/content";
import type { AssetIndexEntry, ContentIndex, LevelIndexEntry } from "../../assets/content";
import { assetDetail, assetLabel } from "../widgets/assetPickerModel";
import { isPlaceholder } from "../format/placeholder";
import { skinListing, skinsDetail } from "../../assets/skinSets";
import type { SkinListing } from "../../assets/skinSets";

/** A folder's path under Content: `Roles`, `Projects`, `Assets`, `Assets/Models`. */
export type FolderId = string;

export interface BrowserFolder {
  readonly id: FolderId;
  /** The last part of the path. */
  readonly label: string;
  readonly parent: FolderId | null;
  /** Nesting under Content: 0 for a top-level folder. */
  readonly depth: number;
  /** Everything under it, subfolders included. */
  readonly count: number;
  readonly holds: "levels" | "assets";
}

/** What the Type filter offers: `Level`, then asset types by name (`Model`, `Texture`, …). */
export type ContentType = string;

export interface LevelTile {
  readonly id: string;
  readonly name: string;
  readonly subtitle: string;
  /** Placeholder subtitle copy (`[tagline]`) renders as secondary text (rule 9). */
  readonly subtitlePlaceholder: boolean;
  /** A role's latest (current or final) title, or null: the tile's title line. */
  readonly position: string | null;
  /** Every title with its years, newest first, then the asset path. */
  readonly tooltip: string;
  readonly assetPath: string;
  /** The thumbnail for each theme mode (the same image when the manifest names one), or null. */
  readonly thumbnail: { readonly dark: string; readonly light: string } | null;
  /** The currently open level gets the OPEN badge. */
  readonly open: boolean;
  /** Position among all tiles: placeholder thumbnails are tinted by a hue step from the accent (A-13). */
  readonly hueStep: number;
}

/** A model in the asset catalog, placeable in the level. */
export interface AssetTile {
  /** Content-relative path: what a Mesh Renderer's asset holds. */
  readonly path: string;
  /** The file name without extension, as Unreal names assets. */
  readonly name: string;
  /** Folder, size, and clip count when it has any. */
  readonly detail: string;
  readonly clips: readonly string[];
  /** A rendered preview (content path, transparent background), or null. */
  readonly thumbnail: string | null;
}

/** What the tile area shows for the chosen folder (null is the Content root). */
export type FolderView =
  | { readonly kind: "folders"; readonly folders: readonly BrowserFolder[] }
  | { readonly kind: "levels"; readonly tiles: readonly LevelTile[] }
  | {
      readonly kind: "assets";
      readonly folders: readonly BrowserFolder[];
      readonly tiles: readonly AssetTile[];
    };

/** Level folders, in order; the asset folders follow them. */
const FOLDER_ORDER = LEVEL_FOLDERS;

/** The type the filter calls levels. */
const LEVEL_TYPE = "Level";

/** Asset kinds by the name the Type filter gives them; other kinds are named after themselves. */
const TYPE_NAMES: Readonly<Record<string, string>> = { gltf: "Model", texture: "Texture" };

/** Every folder, in tree order: the level folders, then the asset folders, each before its subfolders. */
export function browserFolders(index: ContentIndex): BrowserFolder[] {
  const levels = FOLDER_ORDER.map((id): BrowserFolder => ({
    id,
    label: id,
    parent: null,
    depth: 0,
    count: index.levels.filter((level) => folderForKind(level.kind) === id).length,
    holds: "levels",
  }));
  return [...levels, ...assetFolders(listedAssets(index))].filter((folder) => folder.count > 0);
}

// ─── Folding ─────────────────────────────────────────────────────────────

/**
 * Which folders start expanded: none. Every folder with subfolders starts
 * folded, so a growing Assets tree doesn't push Resume.pdf and the level
 * folders out of view (level folders have no subfolders, so they're unaffected).
 */
export function initiallyExpanded(_folders: readonly BrowserFolder[]): ReadonlySet<FolderId> {
  return new Set();
}

/** The folders the tree shows: those whose every ancestor is expanded. */
export function visibleFolders(
  folders: readonly BrowserFolder[],
  expanded: ReadonlySet<FolderId>,
): BrowserFolder[] {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const shown = (folder: BrowserFolder): boolean => {
    if (folder.parent === null) return true;
    const parent = byId.get(folder.parent);
    return parent !== undefined && expanded.has(parent.id) && shown(parent);
  };
  return folders.filter(shown);
}

/** Whether a folder has subfolders (and so a fold toggle). */
export function hasSubfolders(folders: readonly BrowserFolder[], id: FolderId): boolean {
  return folders.some((folder) => folder.parent === id);
}

/** `expanded`, plus every ancestor of `folder`: the tree unfolds to show where the browser is. */
export function expandedFor(
  expanded: ReadonlySet<FolderId>,
  folder: FolderId | null,
  folders: readonly BrowserFolder[],
): ReadonlySet<FolderId> {
  const byId = new Map(folders.map((each) => [each.id, each]));
  const next = new Set(expanded);
  let parent = folder === null ? null : (byId.get(folder)?.parent ?? null);
  while (parent !== null) {
    next.add(parent);
    parent = byId.get(parent)?.parent ?? null;
  }
  return next;
}

export function folderView(
  index: ContentIndex,
  folder: FolderId | null,
  openLevel: string | null,
): FolderView {
  const folders = browserFolders(index);
  if (folder === null) {
    return { kind: "folders", folders: folders.filter((each) => each.parent === null) };
  }

  if (isLevelFolder(folder)) {
    const tiles = levelTiles(index, openLevel).filter((entry) => entry.folder === folder);
    return { kind: "levels", tiles: tiles.map((entry) => entry.tile) };
  }
  return {
    kind: "assets",
    folders: folders.filter((each) => each.parent === folder),
    tiles: listedAssets(index)
      .filter((asset) => assetFolderOf(asset.path) === folder)
      .map((asset) => assetTile(asset, skinListing(index.skinSets))),
  };
}

/** Everything of one type under a folder (or all of Content), flat. */
export function filteredView(
  index: ContentIndex,
  folder: FolderId | null,
  type: ContentType,
  openLevel: string | null,
): FolderView {
  const under = (path: FolderId) =>
    folder === null || path === folder || path.startsWith(`${folder}/`);

  if (type === LEVEL_TYPE) {
    const tiles = levelTiles(index, openLevel).filter((entry) => under(entry.folder));
    return { kind: "levels", tiles: tiles.map((entry) => entry.tile) };
  }
  const assets = listedAssets(index).filter(
    (asset) => typeOfAsset(asset) === type && under(assetFolderOf(asset.path)),
  );
  const listing = skinListing(index.skinSets);
  return { kind: "assets", folders: [], tiles: assets.map((asset) => assetTile(asset, listing)) };
}

/** The types there are, for the Type filter: levels first, then asset types in catalog order. */
export function contentTypes(index: ContentIndex): ContentType[] {
  const types = [
    ...(index.levels.length > 0 ? [LEVEL_TYPE] : []),
    ...index.assets.map(typeOfAsset),
  ];
  return [...new Set(types)];
}

/** The folder a level lives in, so the browser can follow the open level. */
export function folderOfLevel(index: ContentIndex, levelId: string | null): FolderId | null {
  const level = index.levels.find((candidate) => candidate.id === levelId);
  return level ? folderForKind(level.kind) : null;
}

/** Every level's tile with its folder. Hue steps count across folders, so a tile keeps its color. */
function levelTiles(index: ContentIndex, openLevel: string | null) {
  return FOLDER_ORDER.flatMap((id) =>
    index.levels.filter((level) => folderForKind(level.kind) === id),
  ).map((level, step) => ({
    folder: folderForKind(level.kind),
    tile: tileFor(level, openLevel, step),
  }));
}

function isLevelFolder(folder: FolderId): boolean {
  return (FOLDER_ORDER as readonly string[]).includes(folder);
}

/** `assets/models/league_ahri.glb` → `Assets/Models`: the folder on disk, named like the level folders. */
function assetFolderOf(path: string): FolderId {
  return path.split("/").slice(0, -1).map(capitalized).join("/");
}

/** The asset folders on disk, with their ancestors, each before its subfolders. */
function assetFolders(assets: readonly AssetIndexEntry[]): BrowserFolder[] {
  const counts = new Map<FolderId, number>();
  for (const asset of assets) {
    const parts = assetFolderOf(asset.path).split("/");
    parts.forEach((_, i) => {
      const id = parts.slice(0, i + 1).join("/");
      counts.set(id, (counts.get(id) ?? 0) + 1);
    });
  }

  return [...counts.keys()].sort(byTreeOrder).map((id) => {
    const parts = id.split("/");
    return {
      id,
      label: parts.at(-1) ?? id,
      parent: parts.length > 1 ? parts.slice(0, -1).join("/") : null,
      depth: parts.length - 1,
      count: counts.get(id) ?? 0,
      holds: "assets",
    };
  });
}

/** Parents before children, siblings by name: `Assets`, `Assets/Models`, `Assets/Props`. */
function byTreeOrder(a: FolderId, b: FolderId): number {
  const [left, right] = [a.split("/"), b.split("/")];
  for (let i = 0; i < Math.min(left.length, right.length); i++) {
    const order = (left[i] ?? "").localeCompare(right[i] ?? "");
    if (order !== 0) return order;
  }
  return left.length - right.length;
}

function typeOfAsset(asset: AssetIndexEntry): ContentType {
  return TYPE_NAMES[asset.kind] ?? capitalized(asset.kind);
}

function capitalized(name: string): string {
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/** The catalog as lists show it: without a skin set's non-default skins. */
function listedAssets(index: ContentIndex): AssetIndexEntry[] {
  const listing = skinListing(index.skinSets);
  return index.assets.filter((asset) => listing.isListed(asset.path));
}

function assetTile(asset: AssetIndexEntry, listing: SkinListing): AssetTile {
  const clips = asset.clips.length;
  const clipsDetail = clips > 0 ? ` · ${clips} ${clips === 1 ? "clip" : "clips"}` : "";
  return {
    path: asset.path,
    name: assetLabel(asset.path),
    detail: `${assetDetail(asset)}${clipsDetail}${skinsDetail(listing, asset.path)}`,
    clips: asset.clips,
    thumbnail: asset.thumbnail ?? null,
  };
}

function thumbnailOf(level: LevelIndexEntry): LevelTile["thumbnail"] {
  if (level.thumbnail !== undefined) return { dark: level.thumbnail, light: level.thumbnail };
  return level.thumbnails ?? null;
}

function tileFor(level: LevelIndexEntry, openLevel: string | null, hueStep: number): LevelTile {
  return {
    id: level.id,
    name: level.name,
    subtitle: level.subtitle,
    subtitlePlaceholder: isPlaceholder(level.subtitle),
    position: level.positions?.[0]?.title ?? null,
    tooltip: [
      ...(level.positions ?? []).map((position) => `${position.title} · ${position.years}`),
      level.assetPath,
    ].join("\n"),
    assetPath: level.assetPath,
    thumbnail: thumbnailOf(level),
    open: level.id === openLevel,
    hueStep,
  };
}
